import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable, inject, computed, resource, DestroyRef, type Signal } from "@angular/core";
import { Subject, firstValueFrom } from "rxjs";
import {
    INewUserRequestDataModel,
    IUserChangeEvent,
    IUserDeleteResponse,
    IUserResponse,
    IUserUpdateData,
    IUsersListResponse,
    UserDataModel,
} from "../../settings-interfaces";
import { AppSettingsService } from "../app-settings-service/app-settings.service";
import { IFirmUsersService } from "../ifirm-users-service/ifirm-users.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import { TranslateService } from "@ngx-translate/core";

import type { IUsersMap } from "@interfaces/index";
import { UserDashboardSettingsService } from "@shared/user-dashboard-settings-service/user-dashboard-settings.service";
import { type SessionData } from "@shared/auth";
import { DashboardsService } from "../dashboards-service";

export const UnassignedUserId = "00000000-0000-0000-0000-000000000000";
const LegacyNotAssignedToken = "not_assigned";

type UnassignedUserValue = null | undefined | "" | typeof UnassignedUserId | typeof LegacyNotAssignedToken;

export function isUnassigned(userId: string | null | undefined): userId is UnassignedUserValue {
    return !userId || userId === "" || userId === UnassignedUserId || userId === LegacyNotAssignedToken;
}

interface IUsersMapping {
    fromId: Map<string, UserDataModel>;
    fromUserId: Map<string, UserDataModel>;
}

@Injectable({ providedIn: "root" })
export class UsersService {
    private readonly destroyRef = inject(DestroyRef);
    private readonly http = inject(HttpClient);
    private readonly appSettingsService = inject(AppSettingsService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly iFirmUsersService = inject(IFirmUsersService);
    private readonly translateService = inject(TranslateService);
    private readonly userDashboardSettingsService = inject(UserDashboardSettingsService);
    private readonly dashboardsService = inject(DashboardsService);

    Users: UserDataModel[] = [];
    private usersLoadPromise?: Promise<UserDataModel[]>;
    private indexedUsers: UserDataModel[] | undefined;
    private usersByIdentifier = new Map<string, UserDataModel>();

    private powerUsers: Record<string, UserDataModel> = {};

    private readonly usersResource = resource<UserDataModel[], SessionData | undefined>({
        defaultValue: [],
        loader: () => {
            return this.getUsersList();
        },
    });
    public sUsers = computed(() => {
        const users = this.usersResource.value();
        return users;
    });

    readonly usersByIFirmUserId = computed<IUsersMap | undefined>(() => {
        const items = this.sUsers();
        if (!items?.length) return;

        const map: IUsersMap = new Map();
        for (const user of items) map.set(user.IFirmUserId, user);
        return map;
    });

    readonly usersById = computed<IUsersMapping | undefined>(
        () => {
            // Until tracking users are not supported anymore, we will to recompute base on current dashboard
            const useiFirmUserId = this.dashboardsService.currentDashboardUseiFIrmUserId();

            // Track tracking-user list changes so getUserById results stay current
            this.sUsers();
            const iFirmUsers = this.iFirmUsersService.usersById();
            const udsByUserId = this.userDashboardSettingsService.userDashboardSettingsByUserId();
            if (!iFirmUsers?.size || (udsByUserId && !udsByUserId.size)) {
                return;
            }

            const fromId = new Map<string, UserDataModel>();
            const fromUserId = new Map<string, UserDataModel>();
            const disabledFlag = this.translateService.instant("General.Inactive");

            for (const [iFirmUserId, iFirmUser] of iFirmUsers) {
                const baseUserData = this.getUserById(iFirmUserId);
                if (!baseUserData) continue;
                const hasTrackingUser = this.Users.includes(baseUserData);

                const user: UserDataModel = {
                    ...baseUserData,
                    GroupFilteringId1: iFirmUser.GroupFilteringId1,
                    GroupFilteringId2: iFirmUser.GroupFilteringId2,
                    UserCulture: iFirmUser.UserCulture,
                    LicenseEnabled: iFirmUser.LicenseEnabled,
                    completeName: iFirmUser.completeName,
                    restrictedIFirmUserId: iFirmUserId.replace(
                        /^(?:00000000-0000-0000-0000-000000000000|ffffffff-ffff-ffff-ffff-ffffffffffff)\|/,
                        "",
                    ),
                    Active: (useiFirmUserId || baseUserData.Active) && iFirmUser.Active && iFirmUser.LicenseEnabled,
                };

                if (hasTrackingUser) {
                    user.UserDashboardSettings = udsByUserId?.get(baseUserData.Id);
                }

                const displayName = user.completeName?.fullName ?? `${user.FirstName} ${user.LastName}`;
                const reverseDisplayName = `${user.LastName}, ${user.FirstName}`;
                user.FullName = user.Active ? displayName : disabledFlag + displayName;
                user.ReverseFullName = user.Active ? reverseDisplayName : disabledFlag + reverseDisplayName;

                if (hasTrackingUser) fromId.set(baseUserData.Id, user);
                fromUserId.set(iFirmUserId, user);
            }

            return { fromId, fromUserId };
        },
        { equal: (a, b) => this.usersByIdEqual(a, b) },
    );

    private usersByIdEqual(a: IUsersMapping | undefined, b: IUsersMapping | undefined): boolean {
        if (a === b) return true;
        if (!a || !b) return false;
        return this.usersMapEqual(a.fromId, b.fromId) && this.usersMapEqual(a.fromUserId, b.fromUserId);
    }

    private usersMapEqual(a: Map<string, UserDataModel>, b: Map<string, UserDataModel>): boolean {
        if (a.size !== b.size) return false;
        for (const [key, userA] of a) {
            const userB = b.get(key);
            if (!userB || !this.userEqual(userA, userB)) return false;
        }
        return true;
    }

    private userEqual(a: UserDataModel, b: UserDataModel): boolean {
        return (
            a.Id === b.Id &&
            a.IFirmUserId === b.IFirmUserId &&
            a.Active === b.Active &&
            a.FullName === b.FullName &&
            a.ReverseFullName === b.ReverseFullName &&
            a.FirstName === b.FirstName &&
            a.LastName === b.LastName &&
            a.GroupFilteringId1 === b.GroupFilteringId1 &&
            a.GroupFilteringId2 === b.GroupFilteringId2 &&
            a.UserCulture === b.UserCulture &&
            a.LicenseEnabled === b.LicenseEnabled &&
            a.restrictedIFirmUserId === b.restrictedIFirmUserId &&
            a.completeName === b.completeName &&
            a.UserDashboardSettings === b.UserDashboardSettings
        );
    }

    public getUserFromId = computed(() => {
        const users = this.usersById();
        return (id: string) => {
            if (users?.fromId.has(id)) return users.fromId.get(id);
            if (users?.fromUserId.has(id)) return users.fromUserId.get(id);
            return null;
        };
    });

    public getUserNameFromId = computed(() => {
        const getUserFromId = this.getUserFromId();
        return (id: string | undefined | null) => {
            if (!id) return "";
            const user = getUserFromId(id);
            if (!user) return "";

            let name = user.ReverseFullName ?? user.FullName ?? "";
            if (!name) {
                name = user.LastName ?? "";
                if (name.length > 0) name += ", ";
                name += user.FirstName;
            }
            return name;
        };
    });

    MergedUserData = {};
    public userChangedSubject = new Subject<IUserChangeEvent>();
    userChangedEvent$ = this.userChangedSubject.asObservable();

    constructor() {
        this.translateService.onTranslationChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.initPowerUsers();
        });
        this.initPowerUsers();
    }

    private initPowerUsers() {
        this.powerUsers = {
            "sb-c0633ef9-e259-415d-8a2e-ecbe342cc2d1": {
                Id: "sb-c0633ef9-e259-415d-8a2e-ecbe342cc2d1",
                FirstName: this.translateService.instant("General.ApiUserFirstName"),
                LastName: this.translateService.instant("General.ApiUserLastName"),
                Email: "none.api@taxprepdashboard.ca",
            } as UserDataModel,
            "-44": {
                Id: "-44",
                FirstName: this.translateService.instant("General.ApiUserFirstName"),
                LastName: this.translateService.instant("General.ApiUserLastName"),
                Email: "none.api@taxprepdashboard.ca",
            } as UserDataModel,
            "jb-50a0e94b-a715-40eb-b916-bee950bcb478": {
                Id: "jb-50a0e94b-a715-40eb-b916-bee950bcb478",
                FirstName: this.translateService.instant("General.ApiUserFirstName"),
                LastName: this.translateService.instant("General.ApiUserLastName"),
                Email: "none.api@taxprepdashboard.ca",
            } as UserDataModel,
        };
    }

    async refresh(forceReload = false) {
        await this.getUsersList(forceReload);
    }

    async getUsersList(forceReload = false) {
        if (forceReload) {
            this.usersLoadPromise = undefined;
            this.Users = [];
        }
        if (!this.usersLoadPromise) {
            this.usersLoadPromise = this.loadUsers();
        }
        return this.usersLoadPromise;
    }

    async loadUsers() {
        try {
            const result = await firstValueFrom(
                this.http.get<IUsersListResponse>(this.getUrl(), {
                    headers: new HttpHeaders().set("Content-Type", "application/json"),
                }),
            );
            if (result.Code == 200) this.Users = result.Users;
            return result.Users;
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
        return [];
    }

    getUser(userId: string) {
        return this.http.get<IUserResponse>(this.getUrl("users/" + userId));
    }

    addUser(userData: INewUserRequestDataModel) {
        return this.http.post<IUserResponse>(this.getUrl(), userData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    updateUser(userId: string, userData: IUserUpdateData) {
        return this.http.put<IUserResponse>(this.getUrl("users/" + userId), userData, {
            headers: new HttpHeaders().set("Content-Type", "application/json"),
        });
    }

    deleteUser(userId: string) {
        return this.http.delete<IUserDeleteResponse>(this.getUrl("users/" + userId));
    }

    // Matches on either Tracking GUID or iFirm incremental ID
    getUserById(id: string | null, acceptPowerUsers = false) {
        if (id == null || id === "") {
            return null;
        } else {
            const user = this.getTrackingUserById(id);
            if (user) {
                return user;
            }
            // Restricted user Or iFirmUser
            let iFirmUserId = id;
            let powerUserId = id;
            if (iFirmUserId.startsWith("00000000-0000-0000-0000-000000000000|") || id.startsWith("ffffffff-ffff-ffff-ffff-ffffffffffff|")) {
                iFirmUserId = id.slice(37);
            }
            // Power User
            if (powerUserId.startsWith("power-user|")) {
                powerUserId = id.slice(11);
            }
            const iFirmUsers = this.iFirmUsersService.users?.();
            if (iFirmUsers && iFirmUsers.length > 0) {
                // No iFirm Users on User Settings
                const usersById = this.iFirmUsersService.usersById();
                // The shared map keeps the last duplicate; the original lookup kept the first.
                const iFirmUser =
                    usersById && usersById.size < iFirmUsers.length
                        ? iFirmUsers.find((user) => user.UserId.toString() === iFirmUserId)
                        : usersById?.get(iFirmUserId);
                if (iFirmUser) {
                    return {
                        Active: iFirmUser.Active && iFirmUser.LicenseEnabled && !id.startsWith("00000000-0000-0000-0000-000000000000|"),
                        FirstName: iFirmUser.FirstName,
                        LastName: iFirmUser.LastName,
                        Email: iFirmUser.Email,
                        IsSupport: false,
                        IsAdmin: false,
                        IFirmUserId: iFirmUserId,
                        Id: id,
                    } as UserDataModel;
                } else {
                    if (acceptPowerUsers && this.powerUsers[powerUserId]) {
                        return this.powerUsers[powerUserId];
                    }
                    return null;
                }
            } else {
                if (acceptPowerUsers && this.powerUsers[powerUserId]) {
                    return this.powerUsers[powerUserId];
                }
                return null;
            }
        }
    }

    private getTrackingUserById(id: string): UserDataModel | undefined {
        if (this.indexedUsers !== this.Users) {
            this.indexedUsers = this.Users;
            this.usersByIdentifier = new Map();
            for (const user of this.Users) {
                if (!this.usersByIdentifier.has(user.Id)) this.usersByIdentifier.set(user.Id, user);
                if (!this.usersByIdentifier.has(user.IFirmUserId)) this.usersByIdentifier.set(user.IFirmUserId, user);
            }
        }
        return this.usersByIdentifier.get(id);
    }

    private getUrl(path = "users/") {
        const appSettings = this.appSettingsService.appSettings();
        return (appSettings?.UsersService_URL ?? "") + (appSettings?.UsersService_Version ?? "") + path;
    }

    pushUDSIdInMergedUserData(userId: string, dashboardId: string, UDSId: string) {
        if (UDSId && userId && dashboardId && this.MergedUserData[userId] && this.MergedUserData[userId].UserDashboardSettings) {
            if (this.MergedUserData[userId].UserDashboardSettings.length > 0) {
                this.MergedUserData[userId].UserDashboardSettings.forEach((element) => {
                    if (element.DashboardId === dashboardId) {
                        element.Id = UDSId;
                        element.UserId = userId;
                    }
                });
            }
        }
    }

    deleteUDSFromUser(UDSId, userId) {
        if (this.MergedUserData[userId]?.UserDashboardSettings.length > 0) {
            const index = this.MergedUserData[userId].UserDashboardSettings.findIndex((element) => {
                return element.Id === UDSId;
            });

            if (index) {
                this.MergedUserData[userId].UserDashboardSettings.splice(index, 1);
            }
        }
    }
}
